
import { ArrowUpAZ, Building } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { AdminCampaign, SortOption } from "../types";
import { getSortLabel, sortCampaigns } from "../utils";
import { CampaignCard } from "./CampaignCard";

interface CampaignsListProps {
  campaigns: AdminCampaign[];
  sortBy: SortOption;
  setSortBy: (sort: SortOption) => void;
  updating: string | null;
  onUpdateStatus: (campaignId: string, newStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string) => void;
  onDelete?: (campaignId: string) => void;
}

export const CampaignsList = ({ campaigns, sortBy, setSortBy, updating, onUpdateStatus, onDelete }: CampaignsListProps) => {
  const { language } = useLanguage();
  const sortedCampaigns = sortCampaigns(campaigns, sortBy);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-foreground">
          {language === 'ar' ? 'جميع الحملات' : 'All Campaigns'}
        </h2>
        
        <div className="flex items-center gap-2">
          <ArrowUpAZ className="h-4 w-4 text-muted-foreground" />
          <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder={language === 'ar' ? 'اختر طريقة الترتيب' : 'Select sort option'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">{getSortLabel('created_desc', language)}</SelectItem>
              <SelectItem value="created_asc">{getSortLabel('created_asc', language)}</SelectItem>
              <SelectItem value="budget_desc">{getSortLabel('budget_desc', language)}</SelectItem>
              <SelectItem value="budget_asc">{getSortLabel('budget_asc', language)}</SelectItem>
              <SelectItem value="company_asc">{getSortLabel('company_asc', language)}</SelectItem>
              <SelectItem value="company_desc">{getSortLabel('company_desc', language)}</SelectItem>
              <SelectItem value="status_asc">{getSortLabel('status_asc', language)}</SelectItem>
              <SelectItem value="status_desc">{getSortLabel('status_desc', language)}</SelectItem>
              <SelectItem value="devices_desc">{getSortLabel('devices_desc', language)}</SelectItem>
              <SelectItem value="devices_asc">{getSortLabel('devices_asc', language)}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {campaigns.length === 0 ? (
        <Card className="bg-card/80 backdrop-blur-sm border border-border shadow-lg">
          <CardContent className="text-center py-16">
            <Building className="h-24 w-24 mx-auto text-muted-foreground mb-6" />
            <h3 className="text-xl font-semibold text-foreground mb-3">
              {language === 'ar' ? 'لا توجد حملات' : 'No Campaigns Found'}
            </h3>
            <p className="text-muted-foreground">
              {language === 'ar' ? 'لم يتم العثور على أي حملات إعلانية' : 'No advertising campaigns found'}
            </p>
          </CardContent>
        </Card>
      ) : (
        sortedCampaigns.map((campaign) => (
          <CampaignCard
            key={campaign.id}
            campaign={campaign}
            updating={updating}
            onUpdateStatus={onUpdateStatus}
            onDelete={onDelete}
          />
        ))
      )}
    </div>
  );
};
